/**
 * ========================================
 * ONBOARDING GUARD
 * ========================================
 * Sends a signed-in user to /getting-started until they've completed it (admins are never forced -- the backend
 * reports required=false for them). Rendered by ProtectedRoute, so every protected page is covered.
 *
 * Fails OPEN: if the status call errors, the app stays usable rather than locking everyone out, and the failure isn't
 * cached so the next navigation retries.
 */

import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { onboardingService } from '@/services/api/onboarding.service';

export const GETTING_STARTED_PATH = '/getting-started';

interface Props {
  userId?: string;
  children: React.ReactNode;
}

export const OnboardingGuard: React.FC<Props> = ({ userId, children }) => {
  const location = useLocation();
  const [required, setRequired] = useState<boolean | undefined>(
    userId ? onboardingService.getCachedRequired(userId) : undefined
  );

  useEffect(() => {
    if (!userId || required !== undefined) return;
    let cancelled = false;
    onboardingService
      .getStatus()
      .then((s) => {
        onboardingService.setCachedRequired(userId, s.required);
        if (!cancelled) setRequired(s.required);
      })
      .catch(() => {
        if (!cancelled) setRequired(false);
      });
    return () => { cancelled = true; };
  }, [userId, required]);

  if (location.pathname === GETTING_STARTED_PATH) return <>{children}</>;
  if (!userId) return <>{children}</>;

  if (required === undefined) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', color: '#666' }}>
        Loading...
      </div>
    );
  }
  if (required) return <Navigate to={GETTING_STARTED_PATH} replace />;
  return <>{children}</>;
};

export default OnboardingGuard;
